import React, { useState, useEffect } from 'react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

function StarRating({ value, onChange, label }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ color: '#374151', fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>{label}</label>
      <div style={{ display: 'flex', gap: '8px' }}>
        {[1, 2, 3, 4, 5].map(star => (
          <div key={star}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            style={{ fontSize: '28px', cursor: 'pointer', transition: 'transform 0.1s', transform: (hover || value) >= star ? 'scale(1.1)' : 'scale(1)' }}>
            {(hover || value) >= star ? '⭐' : '☆'}
          </div>
        ))}
        <span style={{ color: '#6b7280', fontSize: '13px', marginLeft: '8px', alignSelf: 'center' }}>
          {value ? ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][value] : 'Select rating'}
        </span>
      </div>
    </div>
  );
}

export default function FeedbackPage() {
  const id = new URLSearchParams(window.location.search).get('id');
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    rating: 0, communication: 0, technicalSkills: 0, cultureFit: 0,
    comments: '', decision: '', joiningDate: '', salary: ''
  });

  useEffect(() => {
    if (!id) { setError('Invalid feedback link'); setLoading(false); return; }
    fetch(`${API}/feedback/${id}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setFeedback(d.feedback);
          if (d.feedback.submitted) setDone(true);
        } else setError(d.message);
        setLoading(false);
      }).catch(() => { setError('Failed to load'); setLoading(false); });
  }, []);

  const submit = async () => {
    if (!form.rating || !form.decision) return alert('Please fill rating and decision!');
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/feedback/${id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const d = await r.json();
      if (d.success) setDone(true);
      else alert(d.message);
    } catch { alert('Error submitting!'); }
    setSubmitting(false);
  };

  const base = { fontFamily: '-apple-system,BlinkMacSystemFont,"Inter",sans-serif', minHeight: '100vh', background: '#f4f6f9', padding: '40px 20px' };
  const inp = { width: '100%', padding: '10px 14px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

  if (loading) return <div style={{ ...base, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p style={{ color: '#64748b' }}>Loading...</p></div>;
  if (error) return <div style={{ ...base, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ background: 'white', padding: '40px', borderRadius: '16px', textAlign: 'center' }}><p style={{ color: '#dc2626' }}>{error}</p></div></div>;

  if (done) return (
    <div style={{ ...base, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '48px', maxWidth: '480px', width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎉</div>
        <h2 style={{ color: '#0f172a', fontSize: '24px', fontWeight: '700', margin: '0 0 8px' }}>Feedback Submitted!</h2>
        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 24px' }}>Thank you for your feedback. The Skillora team has been notified.</p>
        <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '16px', border: '1px solid #86efac' }}>
          <p style={{ color: '#16a34a', fontSize: '13px', margin: 0, fontWeight: '600' }}>✅ Your feedback has been recorded successfully</p>
        </div>
      </div>
    </div>
  );

  const candidate = feedback?.candidate;
  const requirement = candidate?.requirement;

  return (
    <div style={base}>
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px' }}>
          <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div>
            <div style={{ color: '#0f172a', fontWeight: '800', fontSize: '16px' }}>Skillora</div>
            <div style={{ color: '#94a3b8', fontSize: '11px' }}>Candidate Feedback Form</div>
          </div>
        </div>

        {/* Candidate Info */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '20px', flexShrink: 0 }}>
            {candidate?.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 style={{ color: '#0f172a', fontSize: '18px', fontWeight: '700', margin: '0 0 4px' }}>{candidate?.name}</h2>
            <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Position: <strong>{requirement?.title}</strong></p>
            {candidate?.aiScore && <p style={{ color: '#6366f1', fontSize: '12px', margin: '4px 0 0', fontWeight: '600' }}>🤖 AI Score: {candidate.aiScore}/10</p>}
          </div>
        </div>

        {/* Ratings */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
          <h3 style={{ color: '#0f172a', fontSize: '15px', fontWeight: '700', margin: '0 0 20px' }}>Rate the Candidate</h3>
          <StarRating value={form.rating} onChange={v => setForm({...form, rating: v})} label="Overall Rating *"/>
          <StarRating value={form.communication} onChange={v => setForm({...form, communication: v})} label="Communication Skills"/>
          <StarRating value={form.technicalSkills} onChange={v => setForm({...form, technicalSkills: v})} label="Technical Skills"/>
          <StarRating value={form.cultureFit} onChange={v => setForm({...form, cultureFit: v})} label="Culture Fit"/>
        </div>

        {/* Decision */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
          <h3 style={{ color: '#0f172a', fontSize: '15px', fontWeight: '700', margin: '0 0 16px' }}>Hiring Decision *</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '20px' }}>
            {[
              { value: 'hired', label: '✅ Hire', color: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
              { value: 'on_hold', label: '⏳ On Hold', color: '#ca8a04', bg: '#fffbeb', border: '#fde68a' },
              { value: 'rejected', label: '❌ Reject', color: '#dc2626', bg: '#fff1f2', border: '#fecaca' },
            ].map(d => (
              <div key={d.value} onClick={() => setForm({...form, decision: d.value})}
                style={{ padding: '14px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center', fontWeight: '600', fontSize: '14px', transition: 'all 0.15s',
                  background: form.decision === d.value ? d.bg : 'white',
                  color: form.decision === d.value ? d.color : '#64748b',
                  border: `2px solid ${form.decision === d.value ? d.border : '#e2e8f0'}` }}>
                {d.label}
              </div>
            ))}
          </div>

          {form.decision === 'hired' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
              <div><label style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>JOINING DATE</label><input type="date" value={form.joiningDate} onChange={e => setForm({...form, joiningDate: e.target.value})} style={inp}/></div>
              <div><label style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>OFFERED SALARY (₹)</label><input type="number" value={form.salary} onChange={e => setForm({...form, salary: e.target.value})} placeholder="50000" style={inp}/></div>
            </div>
          )}

          <div>
            <label style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>COMMENTS</label>
            <textarea value={form.comments} onChange={e => setForm({...form, comments: e.target.value})} placeholder="Share your thoughts about the candidate..." style={{ ...inp, minHeight: '100px', resize: 'vertical' }}/>
          </div>
        </div>

        <button onClick={submit} disabled={submitting || !form.rating || !form.decision}
          style={{ width: '100%', padding: '14px', background: (!form.rating || !form.decision) ? '#e2e8f0' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: (!form.rating || !form.decision) ? '#94a3b8' : 'white', border: 'none', borderRadius: '10px', cursor: (!form.rating || !form.decision) ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: '700', fontFamily: 'inherit' }}>
          {submitting ? 'Submitting...' : '📝 Submit Feedback'}
        </button>
      </div>
    </div>
  );
}
